import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../../utils/tauri';
import { useExtensionStore } from '../../extensions/store';
import { validateManifest } from '../../extensions/registry';
import { listPublished } from '../../extensions/marketplaceClient';
import { marketplaceEnabled } from '../../utils/featureFlags';
import VaultExtensionSettings from '../VaultExtensionSettings';
import TerminalExtensionSettings from '../terminal/TerminalExtensionSettings';
import KnownHostsSettings from '../ssh/KnownHostsSettings';
import MailCatcherExtensionSettings from '../mailCatcher/MailCatcherExtensionSettings';
import DbManagerExtensionSettings from '../dbManager/DbManagerExtensionSettings';
import ChatExtensionSettings from '../chat/ChatExtensionSettings';
import { Section, Row } from '../settingsPrimitives';
import ApiClientExtensionCard from './ApiClientExtensionCard';
import { InstalledExtensionCard } from './InstalledExtensionCard';

/* ── Extension settings pages ──────────────────────────────────────────────────
   Extension-specific settings rendered under Settings → Extensions → <name>.
   Bundled extensions are host-rendered, so their settings UIs are ordinary
   components mapped by extension id here. */
export const EXTENSION_SETTINGS_COMPONENTS = {
  'com.apiboo.vault': VaultExtensionSettings,
  'com.apiboo.terminal': TerminalExtensionSettings,
  // SFTP connections verify host keys too; same list as on the Terminal page.
  'com.apiboo.ftp': KnownHostsSettings,
  'com.apiboo.mail-catcher': MailCatcherExtensionSettings,
  'com.apiboo.chat': ChatExtensionSettings,
  'com.apiboo.db-manager': DbManagerExtensionSettings,
};

/* Per-extension page (Settings → Extensions → <name>): the extension's own
   settings first (if it has any), then the generic manage card. */
export const ExtensionSettingsPage = ({ extensionId, t, onUninstalled }) => {
  const extensions = useExtensionStore((s) => s.extensions);
  const manifest = extensions[extensionId];
  if (!manifest) return null; // uninstalled while open — SettingsModal falls back

  const SettingsComponent = EXTENSION_SETTINGS_COMPONENTS[extensionId];

  // Manage first, then the extension's own settings sections (owner request).
  return (
    <>
      <Section title={t('settings.extensionManageSection', 'Manage')}>
        <InstalledExtensionCard manifest={manifest} onUninstalled={onUninstalled} />
      </Section>
      {SettingsComponent && <SettingsComponent />}
    </>
  );
};

/* ── Extensions tab ────────────────────────────────────────────────────────── */
export const ExtensionsTab = () => {
  const extensions = useExtensionStore((s) => s.extensions);
  const registerExtension = useExtensionStore((s) => s.registerExtension);
  const recordMarketplaceInstall = useExtensionStore((s) => s.recordMarketplaceInstall);

  const [marketplaceList, setMarketplaceList] = useState([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState(null);
  const [consentSlug, setConsentSlug] = useState(null); // slug awaiting install consent, or null
  const [installingSlug, setInstallingSlug] = useState(null);
  const [installError, setInstallError] = useState(null);

  // Phase 6: the marketplace surface is feature-gated OFF by default —
  // first-party extensions ship bundled in the app. The browse/install code
  // below stays intact (future third-party path); flip the flag in dev via
  // localStorage to restore it (see utils/featureFlags.js).
  const showMarketplace = isTauri() && marketplaceEnabled();

  useEffect(() => {
    if (!showMarketplace) return;
    setMarketplaceLoading(true);
    listPublished()
      .then((result) => setMarketplaceList(Array.isArray(result) ? result : []))
      .catch((err) => setMarketplaceError(err.message || 'Failed to reach the marketplace.'))
      .finally(() => setMarketplaceLoading(false));
  }, [showMarketplace]);

  const list = Object.values(extensions).sort((a, b) => a.name.localeCompare(b.name));
  const available = marketplaceList.filter((entry) => !extensions[entry.slug]);

  // REMOVED (deliberately): the "Available Extensions" section, which listed
  // bundled extensions missing from the store and offered to re-Install them
  // from the compiled-in source. It existed only to undo an uninstall that
  // could not really happen, and nothing can populate it any more: bootstrap.js
  // registers every compiled-in extension on every launch, `unregisterExtension`
  // refuses bundled ids, and `undismissBundled` clears any legacy dismissal —
  // so `bundledExtensions.filter((e) => !extensions[e.manifest.id])` is now
  // permanently empty. The one residual way a bundled id could go missing is a
  // manifest that fails `validateManifest` at boot, and re-adding it here would
  // have been actively wrong: this path called `registerExtension` with no
  // validation, so the button would have force-registered the very manifest
  // boot rejected. That is a build-time bug to fix, not a user-facing button.

  const handleInstall = async (entry) => {
    const latest = entry.latest_version;
    if (!latest) return;
    setInstallingSlug(entry.slug);
    setInstallError(null);
    try {
      // Validate before downloading — and reject a manifest whose id doesn't
      // match the marketplace slug, since the id is the registry key: a
      // mismatched entry could overwrite another extension's registration
      // (code swap under an already-trusted identity).
      const { valid, errors } = validateManifest(latest.manifest);
      if (!valid) throw new Error(`Invalid manifest: ${errors.join(', ')}`);
      if (latest.manifest.id !== entry.slug) {
        throw new Error(`Manifest id "${latest.manifest.id}" does not match marketplace slug "${entry.slug}".`);
      }

      const download = await invoke('download_extension_bundle', {
        request: {
          url: latest.bundle_url,
          slug: entry.slug,
          version: latest.version,
          sha256: latest.sha256,
        },
      });

      const fs = await import('@tauri-apps/plugin-fs');
      const entrySource = await fs.readTextFile(download.path);
      const grantedCaps = (latest.manifest.permissions || []).map((p) => p.capability);

      registerExtension(latest.manifest, {
        enabled: true,
        grantedCapabilities: grantedCaps,
        entrySource,
      });
      // The install record is device-global (it mirrors the shared bundle
      // directory); the grants just consented to above stay in this account's
      // namespaced half of the store.
      recordMarketplaceInstall(entry.slug, {
        manifest: latest.manifest,
        version: latest.version,
        bundlePath: download.path,
      });
      setConsentSlug(null);
    } catch (err) {
      setInstallError(err.message || `Failed to install "${entry.name}".`);
    } finally {
      setInstallingSlug(null);
    }
  };

  return (
    <>
      <Section title="Installed Extensions">
        {/* The API Client is built into the app rather than registered as an
            extension, so it is not in `list`; its card is first, where "API
            Client" sorts anyway. One switch for it and History (owner,
            28.10.2026) — the only place it is turned on or off. */}
        <ApiClientExtensionCard />
        {list.map((manifest) => (
          <InstalledExtensionCard key={manifest.id} manifest={manifest} />
        ))}
      </Section>

      {showMarketplace && (
        <Section title="Browse Marketplace">
          {marketplaceLoading && <div className="settings-row-desc">Loading…</div>}
          {marketplaceError && <div className="settings-row-desc text-red-400">{marketplaceError}</div>}
          {!marketplaceLoading && !marketplaceError && available.length === 0 && (
            <div className="settings-row-desc">No new extensions available.</div>
          )}
          {available.map((entry) => {
            const latest = entry.latest_version;
            const isConsenting = consentSlug === entry.slug;
            const isInstalling = installingSlug === entry.slug;
            return (
              <div key={entry.slug} className="settings-section" style={{ paddingTop: 0 }}>
                <Row
                  label={`${entry.name}${latest ? ` · v${latest.version}` : ''}`}
                  desc={entry.description}
                >
                  {!isConsenting && (
                    <button
                      type="button"
                      className="settings-action-btn"
                      disabled={!latest}
                      onClick={() => { setInstallError(null); setConsentSlug(entry.slug); }}
                    >
                      <Download size={13} />
                      Install
                    </button>
                  )}
                </Row>

                {isConsenting && (
                  <div style={{ paddingLeft: 2, paddingBottom: 8 }}>
                    {(latest?.manifest?.permissions || []).length > 0 && (
                      <div className="settings-row-desc" style={{ paddingBottom: 4 }}>
                        This extension requests:
                      </div>
                    )}
                    {(latest?.manifest?.permissions || []).map((perm) => (
                      <Row key={perm.capability} label={perm.capability} desc={perm.reason} />
                    ))}
                    {installError && <div className="settings-row-desc text-red-400">{installError}</div>}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 4 }}>
                      <button
                        type="button"
                        className="settings-action-btn"
                        disabled={isInstalling}
                        onClick={() => handleInstall(entry)}
                      >
                        {isInstalling ? 'Installing…' : 'Confirm Install'}
                      </button>
                      <button
                        type="button"
                        className="settings-action-btn"
                        disabled={isInstalling}
                        onClick={() => { setConsentSlug(null); setInstallError(null); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </Section>
      )}
    </>
  );
};
